import { useId, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useTRPC } from "@/trpc";
import { formatCredentialTimestamp } from "../../lib/format-date";
import {
  RunFindingSummary,
  RunItemProgress,
  RunStatusBadge,
  runFindingLabels,
} from "./run-summary";
import { useRunHistory } from "./use-run-history";
import { useRunCompletionRefresh } from "./use-run-completion-refresh";
import { RunReportDialog } from "./run-report-dialog";
import { StartVerificationDialog } from "./start-verification-dialog";
import {
  ACTIVE_RUN_POLL_MS,
  isRunActive,
  type ListedRun,
  type RenderRunActivity,
  type RenderStartVerification,
  type RunScope,
  type StartedRun,
} from "./types";

type Props = RunScope & {
  open: boolean;
  canEdit: boolean;
  providerName?: string;
  renderRunActivity?: RenderRunActivity;
  renderStartVerification?: RenderStartVerification;
};
type ActiveDialog =
  | { kind: "start" }
  | { kind: "report"; runId: string; request?: StartedRun }
  | null;

export function PrimarySourceRuns({ open, ...props }: Props) {
  if (!open) {
    return null;
  }
  return (
    <ScopedPrimarySourceRuns
      key={`${props.organizationBusinessUnitId}:${props.providerUserId}:${props.credentialViewId ?? "current"}:${props.expectedAffiliationId ?? ""}`}
      {...props}
    />
  );
}

function ScopedPrimarySourceRuns({
  canEdit,
  providerName,
  renderRunActivity,
  renderStartVerification,
  ...scope
}: Omit<Props, "open">) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<ActiveDialog>(null);
  const [showEarlierRuns, setShowEarlierRuns] = useState(false);
  const earlierRunsId = useId();
  const opener = useRef<HTMLButtonElement | null>(null);
  const focusRunId = useRef<string | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  const runButtons = useRef(new Map<string, HTMLButtonElement>());
  const history = useRunHistory({ scope });
  const selectedRunId = dialog?.kind === "report" ? dialog.runId : null;
  const report = useQuery({
    ...trpc.psv.getRun.queryOptions({ ...scope, runId: selectedRunId ?? "" }),
    enabled: !!selectedRunId,
    refetchInterval: (query) =>
      isRunActive(query.state.data?.run) ? ACTIVE_RUN_POLL_MS : false,
  });
  const runs = history.data?.pages.flatMap((page) => page.runs) ?? [];
  const active = runs.some(isRunActive);
  const latestRun = runs[0];
  const earlierRuns = runs.slice(1);
  const hasEarlierRuns = earlierRuns.length > 0 || history.hasNextPage;
  const completedRunId =
    latestRun && !isRunActive(latestRun) ? latestRun.id : null;
  const completedReportId =
    report.data && !isRunActive(report.data.run) ? report.data.run.id : null;
  const {
    organizationBusinessUnitId,
    providerUserId,
    expectedAffiliationId,
    credentialViewId,
  } = scope;
  useRunCompletionRefresh({ scope, completedRunId, completedReportId });

  async function showStartedRun(request: StartedRun) {
    await queryClient.invalidateQueries({
      queryKey: trpc.psv.listRuns.infiniteQueryKey(scope),
    });
    void queryClient.invalidateQueries({
      queryKey: trpc.psv.getProviderScreenings.queryKey(scope),
    });
    focusRunId.current = request.runId;
    setDialog({ kind: "report", runId: request.runId, request });
  }

  function registerRunButton(runId: string, element: HTMLButtonElement | null) {
    if (element) {
      runButtons.current.set(runId, element);
    } else {
      runButtons.current.delete(runId);
    }
  }

  function openReport(runId: string, element: HTMLButtonElement) {
    opener.current = element;
    focusRunId.current = runId;
    setDialog({ kind: "report", runId });
  }

  function restoreFocus() {
    const runRow = focusRunId.current
      ? runButtons.current.get(focusRunId.current)
      : undefined;
    const target = runRow ?? opener.current;
    if (target?.isConnected && !target.disabled) {
      target.focus();
    } else {
      heading.current?.focus();
    }
  }

  return (
    <section
      aria-label="Primary source runs"
      className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-xs"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="size-4 text-primary" aria-hidden="true" />
          <h3
            ref={heading}
            tabIndex={-1}
            className="text-sm font-semibold text-slate-900"
          >
            Primary source runs
          </h3>
        </div>
        {canEdit && !credentialViewId && (
          <Button
            variant="outline"
            size="sm"
            disabled={active || history.isPending || history.isError}
            onClick={(event) => {
              opener.current = event.currentTarget;
              focusRunId.current = null;
              setDialog({ kind: "start" });
            }}
          >
            Run verification
          </Button>
        )}
      </div>
      {history.isLoading && (
        <Skeleton aria-hidden="true" className="h-16 rounded-xl" />
      )}
      {history.isError && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>Couldn’t load verification runs.</p>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            disabled={history.isFetching}
            onClick={() => void history.refetch()}
          >
            Retry runs
          </Button>
        </div>
      )}
      {latestRun && (
        <ul className="divide-y rounded-lg border">
          <RunHistoryRow
            run={latestRun}
            renderRunActivity={renderRunActivity}
            registerButton={registerRunButton}
            onOpen={openReport}
          />
        </ul>
      )}
      {hasEarlierRuns && (
        <div className="space-y-3">
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            aria-expanded={showEarlierRuns}
            aria-controls={earlierRunsId}
            onClick={() => setShowEarlierRuns(!showEarlierRuns)}
          >
            {showEarlierRuns ? "Hide earlier runs" : "Show earlier runs"}
          </Button>
          {showEarlierRuns && (
            <div id={earlierRunsId} className="space-y-3">
              {earlierRuns.length > 0 && (
                <ul className="divide-y rounded-lg border">
                  {earlierRuns.map((run) => (
                    <RunHistoryRow
                      key={run.id}
                      run={run}
                      renderRunActivity={renderRunActivity}
                      registerButton={registerRunButton}
                      onOpen={openReport}
                    />
                  ))}
                </ul>
              )}
              {history.hasNextPage && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  disabled={history.isFetchingNextPage}
                  onClick={() => void history.fetchNextPage()}
                >
                  {history.isFetchingNextPage
                    ? "Loading earlier runs…"
                    : "Load earlier runs"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {!history.isPending && !history.isError && runs.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No primary source runs on record.
        </p>
      )}
      {credentialViewId && (
        <p className="text-xs text-muted-foreground">
          Reports from this previous affiliation. New verification runs are
          available from the current record.
        </p>
      )}
      {dialog?.kind === "start" &&
        canEdit &&
        !credentialViewId &&
        (renderStartVerification ? (
          renderStartVerification({
            scope: {
              organizationBusinessUnitId,
              providerUserId,
              expectedAffiliationId,
            },
            onClose: () => setDialog(null),
            onStarted: showStartedRun,
            onRestoreFocus: restoreFocus,
          })
        ) : (
          <StartVerificationDialog
            scope={{
              organizationBusinessUnitId,
              providerUserId,
              expectedAffiliationId,
            }}
            onClose={() => setDialog(null)}
            onStarted={showStartedRun}
            onRestoreFocus={restoreFocus}
          />
        ))}
      {dialog?.kind === "report" && (
        <RunReportDialog
          renderRunActivity={renderRunActivity}
          canEdit={canEdit}
          key={dialog.runId}
          query={report}
          scope={scope}
          providerName={providerName}
          request={dialog.request}
          onClose={() => setDialog(null)}
          onRestoreFocus={restoreFocus}
        />
      )}
    </section>
  );
}

function RunHistoryRow({
  run,
  renderRunActivity,
  registerButton,
  onOpen,
}: {
  run: ListedRun;
  renderRunActivity?: RenderRunActivity;
  registerButton: (runId: string, element: HTMLButtonElement | null) => void;
  onOpen: (runId: string, element: HTMLButtonElement) => void;
}) {
  const activity = renderRunActivity?.({
    run,
    summary: run.summary,
    itemSummary: run.itemSummary,
  });
  return (
    <li>
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        ref={(element) => registerButton(run.id, element)}
        aria-label={`View report from ${formatCredentialTimestamp(run.createdAt)}`}
        onClick={(event) => onOpen(run.id, event.currentTarget)}
      >
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium">
              {formatCredentialTimestamp(run.createdAt)}
            </span>
            <span className="flex flex-wrap items-center gap-1.5">
              {activity?.label && (
                <span className="text-xs text-muted-foreground">
                  {activity.label}
                </span>
              )}
              {activity?.badge ?? <RunStatusBadge summary={run.summary} />}
            </span>
          </div>
          {activity?.showSourceFindings !== false &&
            (!activity || runFindingLabels(run.summary).length > 0) && (
              <RunFindingSummary summary={run.summary} />
            )}
          {activity?.details}
          {!activity && !isRunActive(run) && (
            <RunItemProgress summary={run.itemSummary} />
          )}
        </div>
        <ChevronRight
          className="size-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
      </button>
    </li>
  );
}
